import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import Notifications from '../components/Notifications';
import PromotionalBanner from '../components/PromotionalBanner';
import BannerSidebar from '../components/BannerSidebar';
import BannerMobileStrip from '../components/BannerMobileStrip';
import { SERVICE_CATEGORIES } from '../constants/categories';
import RoleSwitcher from '../components/RoleSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, AlertCircle, Clock, CheckCircle, User, LogOut, Settings, Crown, Trash2, Star, MessageCircle, ChevronRight, Bell } from 'lucide-react';
import SupportButton from '../components/SupportButton';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, API, language } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');

  useEffect(() => {
    fetchJobs();
    fetchSubscriptionStatus();
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/me/verification-status`);
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to load verification status');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to load subscription status');
    }
  };

  const handlePostJobClick = () => {
    // Check if user has active subscription (clients don't need verification)
    if (!subscriptionStatus?.is_active) {
      toast.error(
        language === 'en' 
          ? 'Please complete subscription to post jobs'
          : language === 'it'
          ? 'Completa l\'abbonamento per pubblicare lavori'
          : 'Bitte schließen Sie das Abonnement ab, um Jobs zu posten'
      );
      navigate('/subscription-plans');
      return;
    }
    navigate('/post-job');
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId, e) => {
    e.stopPropagation(); // Prevent card click
    
    if (!window.confirm(t.deleteConfirm)) {
      return;
    }

    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      toast.success(t.deleteSuccess);
      // Remove job from local state
      setJobs(jobs.filter(job => job.id !== jobId));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t.deleteFailed;
      toast.error(errorMsg);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      assigned: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-4 h-4" />;
    if (status === 'open') return <AlertCircle className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const content = {
    en: {
      title: 'My Orders',
      postJob: 'Post New Order',
      search: 'Search services...',
      categories: 'Browse Categories',
      activeJobs: 'Active Orders',
      noBids: 'No bids yet',
      bids: 'bids',
      viewDetails: 'View Details',
      noJobs: 'No orders posted yet',
      emergency: 'Emergency Service',
      deleteJob: 'Delete',
      deleteConfirm: 'Are you sure you want to delete this order?',
      deleteSuccess: 'Order deleted successfully',
      deleteFailed: 'Failed to delete order',
      assignedTo: 'Assigned to',
      yourRating: 'Your Rating'
    },
    de: {
      title: 'Meine Aufträge',
      postJob: 'Neuen Auftrag erstellen',
      search: 'Dienstleistungen suchen...',
      categories: 'Kategorien durchsuchen',
      activeJobs: 'Aktive Aufträge',
      noBids: 'Noch keine Gebote',
      bids: 'Gebote',
      viewDetails: 'Details anzeigen',
      noJobs: 'Noch keine Aufträge erstellt',
      emergency: 'Notdienst',
      deleteJob: 'Löschen',
      deleteConfirm: 'Möchten Sie diesen Auftrag wirklich löschen?',
      deleteSuccess: 'Auftrag erfolgreich gelöscht',
      deleteFailed: 'Auftrag konnte nicht gelöscht werden',
      assignedTo: 'Zugewiesen an',
      yourRating: 'Ihre Bewertung'
    },
    it: {
      title: 'I Miei Ordini',
      postJob: 'Pubblica Nuovo Ordine',
      search: 'Cerca servizi...',
      categories: 'Sfoglia Categorie',
      activeJobs: 'Ordini Attivi',
      noBids: 'Nessuna offerta ancora',
      bids: 'offerte',
      viewDetails: 'Visualizza Dettagli',
      noJobs: 'Nessun ordine pubblicato ancora',
      emergency: 'Servizio di Emergenza',
      deleteJob: 'Elimina',
      deleteConfirm: 'Sei sicuro di voler eliminare questo ordine?',
      deleteSuccess: 'Ordine eliminato con successo',
      deleteFailed: 'Impossibile eliminare l\'ordine',
      assignedTo: 'Assegnato a',
      yourRating: 'La Tua Valutazione'
    },
    fr: {
      title: 'Mes Commandes',
      postJob: 'Publier Nouvelle Commande',
      search: 'Rechercher des services...',
      categories: 'Parcourir les Catégories',
      activeJobs: 'Commandes Actives',
      noBids: 'Aucune offre encore',
      bids: 'offres',
      viewDetails: 'Voir les Détails',
      noJobs: 'Aucune commande publiée encore',
      emergency: 'Service d\'Urgence',
      deleteJob: 'Supprimer',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cette commande?',
      deleteSuccess: 'Commande supprimée avec succès',
      deleteFailed: 'Impossible de supprimer la commande',
      assignedTo: 'Attribué à',
      yourRating: 'Votre Évaluation'
    }
  };

  const t = content[language] || content.en;

  // Helper function to translate category
  const getCategoryName = (categoryValue) => {
    const category = SERVICE_CATEGORIES.find(c => c.value === categoryValue);
    if (!category) return categoryValue;
    return language === 'en' ? category.en : language === 'it' ? category.it : language === 'fr' ? category.fr : category.de;
  };

  // Helper function to translate urgency
  const getUrgencyLabel = (urgency) => {
    const urgencyMap = {
      en: { low: 'Low', medium: 'Medium', high: 'High', emergency: 'Emergency' },
      de: { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', emergency: 'Notfall' },
      it: { low: 'Bassa', medium: 'Media', high: 'Alta', emergency: 'Emergenza' },
      fr: { low: 'Faible', medium: 'Moyen', high: 'Élevé', emergency: 'Urgence' }
    };
    return urgencyMap[language]?.[urgency] || urgencyMap.en[urgency] || urgency;
  };

  // Helper function to translate status
  const getStatusLabel = (status) => {
    const statusMap = {
      en: { open: 'Open', assigned: 'Assigned', completed: 'Completed', cancelled: 'Cancelled' },
      de: { open: 'Offen', assigned: 'Zugewiesen', completed: 'Abgeschlossen', cancelled: 'Abgebrochen' },
      it: { open: 'Aperto', assigned: 'Assegnato', completed: 'Completato', cancelled: 'Annullato' },
      fr: { open: 'Ouvert', assigned: 'Attribué', completed: 'Terminé', cancelled: 'Annulé' }
    };
    return statusMap[language]?.[status] || statusMap.en[status] || status;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-dashboard">
      {/* Promotional Banner */}
      <PromotionalBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 flex justify-between items-center">
          <div className="flex items-center">
            <img src="/helply-logo.jpg" alt="Helply" className="w-10 h-10 rounded-lg" />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <RoleSwitcher />
            <Button variant="ghost" size="sm" onClick={() => navigate('/chats')} data-testid="chats-btn" className="relative">
              <MessageCircle className="w-5 h-5 text-blue-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} data-testid="notifications-nav-btn" className="relative hidden sm:flex">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="ml-1 text-xs text-gray-600 hidden lg:inline">{language === 'en' ? 'Notifications' : language === 'it' ? 'Notifiche' : language === 'fr' ? 'Notifications' : 'Benachrichtigungen'}</span>
            </Button>
            {user?.is_admin && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="admin-menu-btn" className="text-purple-600">
                <Settings className="w-5 h-5" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscription')} data-testid="subscription-menu-btn">
              <Crown className="w-5 h-5 text-blue-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
            <SupportButton />
            <Notifications />
            <div className="hidden sm:flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium">{user?.name}</span>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">{language === 'en' ? 'Logout' : language === 'it' ? 'Esci' : language === 'fr' ? 'Déconnexion' : 'Abmelden'}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Subscription banners */}
        {subscriptionStatus && subscriptionStatus.status === 'trial' && subscriptionStatus.trial_days_remaining <= 7 && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <Crown className="h-5 w-5 text-orange-600" />
            <AlertDescription className="ml-2">
              <div className="flex justify-between items-center">
                <div>
                  <strong className="text-orange-900">
                    {language === 'en' 
                      ? `Your free trial ends in ${subscriptionStatus.trial_days_remaining} days!` 
                      : language === 'it'
                      ? `La tua prova gratuita termina tra ${subscriptionStatus.trial_days_remaining} giorni!`
                      : language === 'fr'
                      ? `Votre essai gratuit se termine dans ${subscriptionStatus.trial_days_remaining} jours !`
                      : `Ihre kostenlose Testversion endet in ${subscriptionStatus.trial_days_remaining} Tagen!`}
                  </strong>
                  <p className="text-sm text-orange-800 mt-1">
                    {language === 'en' 
                      ? 'Subscribe now to continue using Helply.' 
                      : language === 'it'
                      ? 'Abbonati ora per continuare a usare Helply.'
                      : language === 'fr'
                      ? 'Abonnez-vous maintenant pour continuer à utiliser Helply.'
                      : 'Abonnieren Sie jetzt, um Helply weiterhin zu nutzen.'}
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="subscribe-banner-btn"
                >
                  {language === 'en' ? 'Subscribe' : language === 'it' ? 'Abbonati' : language === 'fr' ? 'S\'abonner' : 'Abonnieren'}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {subscriptionStatus && !subscriptionStatus.is_active && (
          <Alert className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🚀</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 text-lg mb-1">
                  {language === 'en' ? 'Complete Your Setup to Post Jobs!' : language === 'it' ? 'Completa la configurazione per pubblicare lavori!' : language === 'fr' ? 'Complétez votre configuration pour publier des emplois !' : 'Vervollständigen Sie Ihre Einrichtung!'}
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  {language === 'en' 
                    ? 'Choose a subscription plan to unlock all features and start posting jobs!'
                    : language === 'it'
                    ? 'Scegli un piano di abbonamento per sbloccare tutte le funzionalità e iniziare a pubblicare lavori!'
                    : language === 'fr'
                    ? 'Choisissez un plan d\'abonnement pour débloquer toutes les fonctionnalités et commencer à publier des emplois !'
                    : 'Wählen Sie einen Abo-Plan, um alle Funktionen freizuschalten und Jobs zu posten!'}
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-purple-600 text-white">1</div>
                  <span className="text-sm font-medium text-gray-900">
                    {language === 'en' ? 'Choose Plan — CHF 12/year' : language === 'it' ? 'Scegli Piano — CHF 12/anno' : language === 'fr' ? 'Choisir un plan — CHF 12/an' : 'Plan wählen — CHF 12/Jahr'}
                  </span>
                </div>
                <Button onClick={() => navigate('/subscription-plans')} className="bg-purple-600 hover:bg-purple-700" size="sm">
                  <Crown className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Choose Your Plan' : language === 'it' ? 'Scegli il Tuo Piano' : language === 'fr' ? 'Choisir votre plan' : 'Plan wählen'}
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {subscriptionStatus?.is_active && (
          <Alert className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🎉</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-900 text-lg mb-1">
                  {language === 'en' ? 'Welcome to Helply Swiss!' : language === 'it' ? 'Benvenuto su Helply Swiss!' : language === 'fr' ? 'Bienvenue sur Helply Swiss!' : 'Willkommen bei Helply Swiss!'}
                </h3>
                <p className="text-sm text-green-800">
                  {language === 'en' 
                    ? 'The app where you can save time and find the right expert for your needs.'
                    : language === 'it'
                    ? 'L\'app dove puoi risparmiare tempo e trovare l\'esperto giusto per le tue esigenze.'
                    : language === 'fr'
                    ? 'L\'application où vous pouvez gagner du temps et trouver le bon expert pour vos besoins.'
                    : 'Die App, mit der Sie Zeit sparen und den richtigen Experten für Ihre Bedürfnisse finden.'}
                </p>
              </div>
            </div>
          </Alert>
        )}

        {/* Search & Post Job */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <Input
              placeholder={t.search}
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6"
            onClick={handlePostJobClick}
            data-testid="post-job-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t.postJob}
          </Button>
        </div>

        {/* 3-column layout: Categories | Jobs | Banners */}
        <div className="flex gap-6">

          {/* LEFT: Categories Sidebar */}
          <aside className="hidden sm:block w-44 flex-shrink-0" data-testid="categories-sidebar">
            <div className="bg-white rounded-xl border border-gray-200 sticky top-20">
              <div className="p-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-800 mb-2">{t.categories}</h2>
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Filter...' : language === 'de' ? 'Filtern...' : language === 'it' ? 'Filtra...' : 'Filtrer...'}
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                {/* All categories option */}
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-blue-50 transition-colors ${!selectedCategory ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                  data-testid="category-all"
                >
                  <span>{language === 'en' ? 'All Categories' : language === 'de' ? 'Alle Kategorien' : language === 'it' ? 'Tutte le categorie' : 'Toutes catégories'}</span>
                  {!selectedCategory && <ChevronRight className="w-3 h-3" />}
                </button>
                {SERVICE_CATEGORIES
                  .filter(cat => {
                    const name = language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de;
                    return name.toLowerCase().includes(categorySearch.toLowerCase());
                  })
                  .map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        setSelectedCategory(cat.value === selectedCategory ? null : cat.value);
                        if (!subscriptionStatus?.is_active) {
                          handlePostJobClick();
                        } else {
                          navigate('/post-job', { state: { category: cat.value } });
                        }
                      }}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-blue-50 transition-colors border-t border-gray-50 ${selectedCategory === cat.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600'}`}
                      data-testid={`category-sidebar-${cat.value}`}
                    >
                      <span className="line-clamp-2 leading-tight">
                        {language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de}
                      </span>
                      {selectedCategory === cat.value && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                    </button>
                  ))}
              </div>
            </div>
          </aside>

          {/* CENTER: Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile categories (horizontal scroll) — only shown when left sidebar is hidden */}
            <div className="sm:hidden mb-4 overflow-x-auto">
              <div className="flex gap-2 pb-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!selectedCategory ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
                >
                  {language === 'en' ? 'All' : language === 'de' ? 'Alle' : language === 'it' ? 'Tutti' : 'Tous'}
                </button>
                {SERVICE_CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value === selectedCategory ? null : cat.value)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedCategory === cat.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    {language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Jobs */}
            <div>
              <h2 className="text-lg font-bold font-heading mb-4">
                {selectedCategory 
                  ? getCategoryName(selectedCategory)
                  : t.activeJobs}
              </h2>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : jobs.filter(job => {
                const matchesSearch = !searchQuery || job.title?.toLowerCase().includes(searchQuery.toLowerCase()) || job.description?.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesCategory = !selectedCategory || job.category === selectedCategory;
                return matchesSearch && matchesCategory;
              }).length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-gray-500">{t.noJobs}</p>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-2 gap-4">
                  {jobs
                    .filter(job => {
                      const matchesSearch = !searchQuery || job.title?.toLowerCase().includes(searchQuery.toLowerCase()) || job.description?.toLowerCase().includes(searchQuery.toLowerCase());
                      const matchesCategory = !selectedCategory || job.category === selectedCategory;
                      return matchesSearch && matchesCategory;
                    })
                    .map((job) => (
                    <Card key={job.id} className="hover-lift cursor-pointer" onClick={() => navigate(`/job/${job.id}`)} data-testid={`job-card-${job.id}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-lg font-heading">{job.title}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(job.status)}>
                              {getStatusIcon(job.status)}
                              <span className="ml-1">{getStatusLabel(job.status)}</span>
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteJob(job.id, e)}
                              className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                              data-testid={`delete-job-btn-${job.id}`}
                              title={language === 'en' ? 'Delete job' : language === 'it' ? 'Elimina lavoro' : 'Auftrag löschen'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{job.description}</p>
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-bold text-blue-600">CHF {job.budget}</span>
                          <span className="text-gray-500">{getUrgencyLabel(job.urgency)}</span>
                        </div>
                        {job.status === 'open' && (
                          <p className="text-xs text-gray-500 mt-2">
                            {job.bids?.length > 0 ? `${job.bids.length} ${t.bids}` : t.noBids}
                          </p>
                        )}
                        {job.status === 'assigned' && job.assigned_pro_name && job.professional_rating && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-500 mb-1">{t.assignedTo}</p>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">{job.assigned_pro_name}</p>
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm font-bold">{job.professional_rating.toFixed(1)}</span>
                                <span className="text-xs text-gray-500">({job.professional_reviews})</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {job.status === 'completed' && job.rating && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">{t.yourRating}:</p>
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star 
                                    key={star}
                                    className={`w-4 h-4 ${star <= job.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Banners Sidebar — desktop */}
          <aside className="hidden sm:block w-44 flex-shrink-0" data-testid="banner-sidebar">
            <div className="sticky top-20">
              <BannerSidebar />
            </div>
          </aside>

        </div>

        {/* Mobile Banners — shown below jobs on phone only */}
        <BannerMobileStrip />
      </main>
    </div>
  );
};

export default ClientDashboard;